/**
 * The documentation navigation band's geometry, as a pure function.
 *
 * WHAT THIS REPLACES. The vanilla band laid itself out by writing to the DOM and reading it
 * back in the same frame — `layoutDocNav()` set `marginTop='0px'`, called
 * `getBoundingClientRect()`, then wrote the measured offset back; `updateParentDocClearance()`
 * wrote a CSS custom property from a measured overlap; `drawDocNavWires()` re-measured every
 * element against `.docstage` and assembled an SVG string. Three reset-measure-write passes on
 * one frame, each forcing a synchronous reflow.
 *
 * It is the same shape as the map's layout circularity, which is why the plan calls this "the
 * rehearsal": measurement is an INPUT, not a step inside the algorithm. A `ResizeObserver`
 * collects rectangles, this function turns them into an offset, a clearance and a list of
 * paths, and React renders all three. Nothing here touches the DOM, so it is testable against
 * numbers instead of against a browser.
 *
 * THE RESET PASS IS GONE, and that is the one non-obvious change. The original could only ask
 * "where would the children column sit at margin 0?" by actually putting it there first. Here
 * the caller reports the offset it currently has applied, and the residual correction measured
 * AT that offset is added to it — `applied + (currentCentre - childCentre)` is algebraically
 * the same number the reset pass produced, without the write. It converges in one step because
 * the correction is exact, and the same >0.5px guard the original used stops sub-pixel
 * oscillation.
 *
 * Wire paths are computed for where the children WILL be, not where they are: the column moves
 * as a block (one grid item with `overflow:auto`, so its contents do not reflow), which makes
 * shifting every child rectangle by the pending delta exact. That is a frame, not a
 * correctness property — the next measurement would fix the wires anyway, because by then the
 * column has moved and the delta is zero. What it buys is that the wires are never briefly
 * drawn at the old positions, and that the whole band settles in one render instead of two.
 */

/** A measured rectangle, in viewport coordinates — a `DOMRect`'s four edges. */
export interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface DocNavMeasurements {
  /** `.docstage` — the origin every other rectangle is expressed relative to. */
  stage: Rect;
  /** The current node's bubble. Absent means there is nothing to draw. */
  current: Rect | null;
  /** The parent node's bubble, when the focus node has a visible parent. */
  parent: Rect | null;
  /** `.docframe-wrap`, the page the child wires route around. */
  page: Rect | null;
  /** One per child, in the order they are stacked. */
  children: readonly { id: string; rect: Rect }[];
  /**
   * The parent's floating doc column — `null` when there is none or it is empty.
   *
   * It is positioned out of flow and hangs into the grid row below, which is where the log
   * panel lives; that overhang is the only reason a clearance exists.
   */
  parentDocs: Rect | null;
  /** The log panel, or `null` when it is not on screen. */
  logs: Rect | null;
  /** The offset currently applied to the children column. See the note above. */
  appliedChildrenOffset: number;
}

export interface DocNavWire {
  key: string;
  d: string;
  color: string;
}

export interface DocNavLayout {
  /** Size for the wire canvas, matching the stage it overlays. */
  width: number;
  height: number;
  /** `margin-top` for the children column, so the first child sits on the current node's spine. */
  childrenOffset: number;
  /** `--pdocs-h`, or `null` to leave the property unset (the stylesheet defaults it to 0). */
  parentDocClearance: number | null;
  wires: DocNavWire[];
}

/** Below this the offset is treated as already correct — the original's own guard. */
const OFFSET_EPSILON = 0.5;
/** Breathing room under the parent's floating pills, on top of the measured overlap. */
const LOGS_CLEARANCE_PAD = 10;
/** Where the vertical trunk goes when there is no page rectangle to measure against. */
const PAGE_FALLBACK_GAP = 80;
/** Corner rounding on the trunk, clamped so a tight turn cannot invert. */
const TRUNK_MIN_R = 2;
const TRUNK_MAX_R = 16;
/**
 * A child within this many pixels of the current node's centre gets a straight wire.
 *
 * Not zero: the first child is nudged ONTO the spine, and floating-point centres do not land
 * exactly equal — without the tolerance the straight branch would be drawn as a 0.4px jog.
 */
const STRAIGHT_TOLERANCE = 2;

const centreY = (r: Rect): number => (r.top + r.bottom) / 2;
const shifted = (r: Rect, dy: number): Rect => ({ ...r, top: r.top + dy, bottom: r.bottom + dy });

/**
 * How far to nudge the children column.
 *
 * Exported separately because it is the half a test can state as an identity: whatever offset
 * is already applied, the answer is the same.
 */
export function childrenColumnOffset(m: DocNavMeasurements): number {
  const first = m.children[0];
  if (!m.current || !first) return m.appliedChildrenOffset;
  const residual = centreY(m.current) - centreY(first.rect);
  const off = m.appliedChildrenOffset + residual;
  return Math.abs(off) > OFFSET_EPSILON ? off : 0;
}

/**
 * Clearance for the log panel under the parent's floating pills.
 *
 * MEASURED rather than computed from the pill height, and deliberately: the parent bubble is
 * vertically centred in its grid row, so how far its pills actually reach depends on how tall
 * that row turned out. `.doclogs` is `align-self:start`, so its own box top does not move when
 * this padding changes — which is what stops the measurement feeding back into itself.
 */
export function parentDocClearance(m: DocNavMeasurements): number | null {
  if (!m.parentDocs || !m.logs) return null;
  return Math.max(0, Math.ceil(m.parentDocs.bottom - m.logs.top) + LOGS_CLEARANCE_PAD);
}

export function computeDocNavLayout(
  m: DocNavMeasurements,
  colorOf: (id: string | null) => string,
  currentId: string | null,
): DocNavLayout {
  const width = m.stage.right - m.stage.left;
  const height = m.stage.bottom - m.stage.top;
  const childrenOffset = childrenColumnOffset(m);

  const base: DocNavLayout = {
    width,
    height,
    childrenOffset,
    parentDocClearance: parentDocClearance(m),
    wires: [],
  };
  if (!m.current) return base;

  /** Stage-relative edges. `cy` is the vertical centre, which is what every wire anchors to. */
  const rel = (r: Rect) => ({
    l: r.left - m.stage.left,
    r: r.right - m.stage.left,
    cy: centreY(r) - m.stage.top,
  });

  const c = rel(m.current);
  const wires: DocNavWire[] = [];

  // Parent -> current: a midpoint cubic, the same curve the map draws between a node and its
  // parent. Coloured by the CURRENT node, because the wire is what leads TO it.
  if (m.parent) {
    const p = rel(m.parent);
    const mx = (p.r + c.l) / 2;
    wires.push({
      key: 'parent',
      d: `M${p.r},${p.cy} C${mx},${p.cy} ${mx},${c.cy} ${c.l},${c.cy}`,
      color: colorOf(currentId),
    });
  }

  // Current -> each child: straight right across the top of the page, then down a single
  // trunk in the gutter to the right of it, then back out into each child. One trunk rather
  // than one curve each, so a node with eight children does not become a fan of crossings.
  if (m.children.length) {
    const delta = childrenOffset - m.appliedChildrenOffset;
    const kids = m.children.map((k) => ({ id: k.id, r: rel(shifted(k.rect, delta)) }));
    const minL = Math.min(...kids.map((k) => k.r.l));
    const pageR = m.page ? rel(m.page).r : c.r + PAGE_FALLBACK_GAP;
    const jx = Math.round((pageR + minL) / 2);

    for (const k of kids) {
      const cy = k.r.cy;
      let d: string;
      if (cy <= c.cy + STRAIGHT_TOLERANCE) {
        d = `M${c.r},${c.cy} H${k.r.l}`;
      } else {
        const r = Math.max(TRUNK_MIN_R, Math.min(TRUNK_MAX_R, (cy - c.cy) / 2, (k.r.l - jx) / 2));
        d =
          `M${c.r},${c.cy} H${jx - r} Q${jx},${c.cy} ${jx},${c.cy + r}` +
          ` V${cy - r} Q${jx},${cy} ${jx + r},${cy} H${k.r.l}`;
      }
      wires.push({ key: k.id, d, color: colorOf(k.id) });
    }
  }

  return { ...base, wires };
}
